import type { ReactNode } from "react";
import { Link } from "react-router";
import { Chevron } from "../../../shared/phone/Row";
import { CHANNELS } from "../../../shared/goals/channels";
import { metaSettings } from "../../../shared/goals/meta";
import { Icon } from "../../components/ui/Icon";
import type { ReadyLite, ReadyStep } from "../../lib/ccApi";
import type { GoalView } from "../../lib/goalsApi";
import { andList, fmt, usd } from "./text";
import { useCan } from "../../context/AuthContext";

/** What the owner does for each step, and where: the part of Get ready that settles it. */
const ACTS: Record<string, [string, string]> = {
  meta: ["Connect Meta", "ready"],
  pixel: ["Pick the Meta pixel", "ready"],
  currency: ["Pick the ad account", "ready"],
  audience: ["Choose who sees the ads", "audience"],
  link: ["Add the page", "ads"],
  tracking: ["Check the tracking", "ads"],
  learns: ["Pick what Meta aims for", "ads"],
  daily: ["Set the daily budget", "ads"],
  ads: ["Finish the ads", "ads"],
  formats: ["Fix the ad files", "ads"],
  emails: ["Approve the first emails", "ready"],
  postal: ["Add your postal address", "ready"],
};
const actOf = (s: ReadyStep, goalPath: string) => {
  const [act, part] = ACTS[s.id] ?? ["Open Get ready", "ready"];
  return { act, to: `${goalPath}/${part}` };
};

/** What the agent is still writing or building for Get ready: shown, never counted as a step. */
function busyRows(ready: ReadyLite | null): { key: string; title: string; sub: string }[] {
  if (!ready) return [];
  const rows: { key: string; title: string; sub: string }[] = [];
  if (ready.ads.some((a) => a.status === "drafting")) rows.push({ key: "draft", title: "Ads", sub: "Writing the ads' words" });
  if (ready.emailOn && !ready.emails.length) rows.push({ key: "write", title: "Cold email", sub: "Writing the emails" });
  for (const l of ready.lists.filter((x) => x.state === "queued" || x.state === "running")) rows.push({ key: `l-${l.market}`, title: "Cold email", sub: `Building the ${l.market.split(",")[0]} list` });
  return rows;
}

/** The chat's getting-ready rows: each step still open, by name, and what the agent is on. */
/** A row into a step on a phone: a link for an Editor, its words only for anyone else. */
function StepRow({ to, className, children }: { to: string; className: string; children: ReactNode }) {
  return useCan()("editor") ? (
    <Link className={className} to={to}>
      {children}
      <Chevron />
    </Link>
  ) : (
    <div className={`${className} static`}>{children}</div>
  );
}

/** A heading that opens its page for an Editor, and is only words for anyone else. */
function HeadLink({ to, children }: { to: string; children: ReactNode }) {
  return useCan()("editor") ? <Link to={to}>{children}</Link> : <>{children}</>;
}

export function ReadyRows({ goalPath, steps, ready }: { goalPath: string; steps: ReadyStep[]; ready: ReadyLite | null }) {
  const may = useCan()("editor");
  const open = steps.filter((s) => !s.ok);
  const busy = busyRows(ready);
  if (!open.length && !busy.length)
    return (
      <p className="cc-fine" style={{ margin: 0 }}>
        Nothing is waiting for you. Launch the ads at the bottom of the Ads step when you are ready.
      </p>
    );
  return (
    <div className="cc-nrows">
      {open.map((s) => {
        const { act, to } = actOf(s, goalPath);
        return (
          <div className="cc-nr" key={s.id}>
            <span className="cc-nri">
              <Icon name="clock" />
            </span>
            <span className="cc-nrt">
              <b>{act}</b>
              <span>{s.detail ?? s.label}</span>
            </span>
            {may && (
              <Link className="btn sm" to={to}>
                Open
              </Link>
            )}
          </div>
        );
      })}
      {busy.map((r) => (
        <div className="cc-nr done" key={r.key}>
          <span className="cc-nri">
            <Icon name="spark" />
          </span>
          <span className="cc-nrt">
            <b>{r.title}</b>
            <span>{r.sub}</span>
          </span>
          <span className="pill live">The agent is on it</span>
        </div>
      ))}
    </div>
  );
}

/** The goal page while it gets ready: every step before launch, the open ones first, then what goes live and what the plan expects. */
export function ReadyChecklist({ goalPath, steps, ready, view }: { goalPath: string; steps: ReadyStep[] | null; ready: ReadyLite | null; view: GoalView }) {
  const may = useCan()("editor");
  const config = view.current?.config ?? null;
  const plan = view.current?.plan ?? null;
  const metaOn = !!config?.channels.meta?.on;
  const emailOn = !!config?.channels.email?.on;
  const open = (steps ?? []).filter((s) => !s.ok);
  const done = (steps ?? []).filter((s) => s.ok);
  const title = open.length === 0 && steps?.length ? "Ready to launch" : metaOn ? "Before the ads go live" : emailOn ? "Before the emails go out" : "Before launch";
  const busy = busyRows(ready);

  const settings = metaOn ? metaSettings(config?.channels.meta?.settings) : null;
  const daily = settings?.dailyCents ?? config?.money.dailyMaxCents ?? null;
  const ads = (ready?.ads ?? []).filter((a) => a.status !== "removed" && a.status !== "drafting").map((a) => String(a.words.name ?? "").trim()).filter(Boolean);
  const live: [string, string][] = [];
  if (metaOn) {
    if (ads.length) live.push([ads.length === 1 ? "The ad" : "The ads", andList(ads)]);
    if (daily && config) live.push(["Daily budget", `${usd(daily)} a day, up to ${usd(config.money.budgetCents)}`]);
    live.push(["Sends people to", settings?.opens === "metaform" ? "Meta's form" : (settings?.link ?? "").replace(/^https?:\/\//, "") || "No page yet"]);
  }
  if (emailOn) live.push([CHANNELS.email.label, andList(config?.channels.email?.markets.map((m) => m.split(",")[0]) ?? []) || "No market yet"]);
  const head = plan?.headline;

  return (
    <>
      <section className="card rg-list" aria-labelledby="rg-h">
        <div className="rg-lh">
          <h2 id="rg-h">{title}</h2>
          {steps && steps.length > 0 && (
            <span className="rg-count">
              <b>{done.length}</b> of {steps.length} ready
            </span>
          )}
        </div>
        {steps === null ? (
          <p className="rs-hint">Reading what is left…</p>
        ) : (
          <>
            {steps.length > 0 && (
              <div className="rg-seg" aria-hidden="true">
                {steps.map((s) => (
                  <i key={s.id} className={s.ok ? "ok" : "w"} />
                ))}
              </div>
            )}
            <ol className="rg-steps">
              {open.map((s, i) => {
                const { act, to } = actOf(s, goalPath);
                // Why: the first open step is the one to do now; the rest wait their turn, quieter.
                return (
                  <li className={`rg-s ${i === 0 ? "wait" : "todo"}`} key={s.id}>
                    <span className="rg-mk">
                      <i />
                    </span>
                    <span className="rg-st">
                      <b>{s.label}</b>
                      {s.detail && <em>{s.detail}</em>}
                    </span>
                    {may && (
                      <Link className={`btn sm${i === 0 ? "" : " ghost"}`} to={to}>
                        {act}
                        <Icon name="right" />
                      </Link>
                    )}
                  </li>
                );
              })}
              {busy.map((r) => (
                <li className="rg-s agent" key={r.key}>
                  <span className="rg-mk">
                    <Icon name="spark" />
                  </span>
                  <span className="rg-st">
                    <b>{r.title}</b>
                    <em>{r.sub}</em>
                  </span>
                  <span className="pill live">The agent is on it</span>
                </li>
              ))}
              {done.map((s) => (
                <li className="rg-s ok" key={s.id}>
                  <span className="rg-mk">
                    <Icon name="check" />
                  </span>
                  <span className="rg-st">
                    <b>{s.label}</b>
                    {s.detail && <em>{s.detail}</em>}
                  </span>
                </li>
              ))}
              {open.length === 0 && steps.length > 0 && (
                <li className="rg-s wait">
                  <span className="rg-mk">
                    <i />
                  </span>
                  <span className="rg-st">
                    <b>Turn the ads on</b>
                    <em>Everything is ready. Launch them at the bottom of the Ads step.</em>
                  </span>
                  {may && (
                    <Link className="btn sm" to={`${goalPath}/ads`}>
                      Open the Ads step
                      <Icon name="right" />
                    </Link>
                  )}
                </li>
              )}
            </ol>
          </>
        )}
      </section>
      {(live.length > 0 || head) && (
        <div className={`rg-two${live.length > 0 && head ? "" : " one"}`}>
          {live.length > 0 && (
            <section className="card rg-mini">
              <h3>
                <HeadLink to={`${goalPath}/${metaOn ? "ads" : "ready"}`}>
                  What goes live
                  {may && <Icon name="right" />}
                </HeadLink>
              </h3>
              <dl>
                {live.map(([k, v]) => (
                  <div key={k}>
                    <dt>{k}</dt>
                    <dd>{v}</dd>
                  </div>
                ))}
              </dl>
            </section>
          )}
          {head && config && (
            <section className="card rg-mini">
              <h3>
                <Link to={`${goalPath}/plan`}>
                  What the plan expects
                  <Icon name="right" />
                </Link>
              </h3>
              <dl>
                <div>
                  <dt>Results</dt>
                  <dd>
                    {head.expected} of {fmt(config.result.target)}
                  </dd>
                </div>
                <div>
                  <dt>Each</dt>
                  <dd>{head.cost}</dd>
                </div>
                <div>
                  <dt>Pace</dt>
                  <dd>{head.pace}</dd>
                </div>
              </dl>
            </section>
          )}
        </div>
      )}
    </>
  );
}

/**
 * Getting ready on a phone and an iPad: how many steps are ready, every step as a row (the one to do now first,
 * then the rest, then what the agent is on and what is done), and what goes live and what the plan expects.
 */
export function ReadyNative({ goalPath, steps, ready, view }: { goalPath: string; steps: ReadyStep[] | null; ready: ReadyLite | null; view: GoalView }) {
  const config = view.current?.config ?? null;
  const plan = view.current?.plan ?? null;
  const metaOn = !!config?.channels.meta?.on;
  const emailOn = !!config?.channels.email?.on;
  const open = (steps ?? []).filter((s) => !s.ok);
  const done = (steps ?? []).filter((s) => s.ok);
  const title = open.length === 0 && steps?.length ? "Ready to launch" : metaOn ? "Before the ads go live" : emailOn ? "Before the emails go out" : "Before launch";
  const busy = busyRows(ready);
  const settings = metaOn ? metaSettings(config?.channels.meta?.settings) : null;
  const daily = settings?.dailyCents ?? config?.money.dailyMaxCents ?? null;
  const ads = (ready?.ads ?? []).filter((a) => a.status !== "removed" && a.status !== "drafting").map((a) => String(a.words.name ?? "").trim()).filter(Boolean);
  const live: [string, string][] = [];
  if (metaOn) {
    if (ads.length) live.push([ads.length === 1 ? "The ad" : "The ads", andList(ads)]);
    if (daily && config) live.push(["Daily budget", `${usd(daily)} a day, up to ${usd(config.money.budgetCents)}`]);
    live.push(["Sends people to", settings?.opens === "metaform" ? "Meta's form" : (settings?.link ?? "").replace(/^https?:\/\//, "") || "No page yet"]);
  }
  if (emailOn) live.push([CHANNELS.email.label, andList(config?.channels.email?.markets.map((m) => m.split(",")[0]) ?? []) || "No market yet"]);
  const head = plan?.headline;

  return (
    <>
      <section aria-labelledby="gp-rg-h">
        <div className="pn-sh">
          <h2 id="gp-rg-h">{title}</h2>
          {steps && steps.length > 0 && (
            <span className="gp-count">
              {done.length} of {steps.length} ready
            </span>
          )}
        </div>
        {steps && steps.length > 0 && (
          <div className="gp-prog" aria-hidden="true">
            {steps.map((s) => (
              <i key={s.id} className={s.ok ? "ok" : undefined} />
            ))}
          </div>
        )}
        {steps === null ? (
          <p className="pn-note">Reading what is left…</p>
        ) : (
          <div className="pn-grp">
            {open.map((s, i) => {
              const { act, to } = actOf(s, goalPath);
              return (
                <StepRow className={`pn-row wrap${i === 0 ? " gp-now" : ""}`} to={to} key={s.id}>
                  <span className={`pn-ico${i === 0 ? " warn" : " plain"}`}>
                    <Icon name="clock" />
                  </span>
                  <span className="m">
                    <span className="t">{act}</span>
                    <span className="s">{s.detail ?? s.label}</span>
                  </span>
                </StepRow>
              );
            })}
            {open.length === 0 && steps.length > 0 && (
              <StepRow className="pn-row wrap gp-now" to={`${goalPath}/ads`}>
                <span className="pn-ico warn">
                  <Icon name="play" />
                </span>
                <span className="m">
                  <span className="t">Turn the ads on</span>
                  <span className="s">Everything is ready. Launch them at the bottom of the Ads step.</span>
                </span>
              </StepRow>
            )}
            {busy.map((r) => (
              <div className="pn-row static wrap" key={r.key}>
                <span className="pn-ico violet">
                  <Icon name="spark" />
                </span>
                <span className="m">
                  <span className="t">{r.title}</span>
                  <span className="s">{r.sub}</span>
                </span>
                <span className="pn-pill violet">On it</span>
              </div>
            ))}
            {done.map((s) => (
              <div className="pn-row static wrap" key={s.id}>
                <span className="pn-ico good">
                  <Icon name="check" />
                </span>
                <span className="m">
                  <span className="t">{s.label}</span>
                  {s.detail && <span className="s">{s.detail}</span>}
                </span>
              </div>
            ))}
          </div>
        )}
      </section>
      {live.length > 0 && (
        <section>
          <div className="pn-sh">
            <h2>
              <HeadLink to={`${goalPath}/${metaOn ? "ads" : "ready"}`}>What goes live</HeadLink>
            </h2>
          </div>
          <div className="pn-grp">
            {live.map(([k, v]) => (
              <div className="pn-row static wrap" key={k}>
                <span className="m">
                  <span className="s">{k}</span>
                  <span className="t">{v}</span>
                </span>
              </div>
            ))}
          </div>
        </section>
      )}
      {head && config && (
        <section>
          <div className="pn-sh">
            <h2>
              <Link to={`${goalPath}/plan`}>What the plan expects</Link>
            </h2>
          </div>
          <div className="pn-grp">
            <div className="pn-row static">
              <span className="m">
                <span className="t">Results</span>
              </span>
              <span className="v">
                {head.expected} of {fmt(config.result.target)}
              </span>
            </div>
            <div className="pn-row static">
              <span className="m">
                <span className="t">Each</span>
              </span>
              <span className="v">{head.cost}</span>
            </div>
            <div className="pn-row static">
              <span className="m">
                <span className="t">Pace</span>
              </span>
              <span className="v">{head.pace}</span>
            </div>
          </div>
        </section>
      )}
    </>
  );
}
